import React, { useState, useContext, useEffect, useRef } from "react";
import { Link as RouterLink } from "react-router-dom";
import Button from "@material-ui/core/Button";
import CssBaseline from "@material-ui/core/CssBaseline";
import TextField from "@material-ui/core/TextField";
import Link from "@material-ui/core/Link";
import Grid from "@material-ui/core/Grid";
import Typography from "@material-ui/core/Typography";
import { makeStyles, useTheme } from "@material-ui/core/styles";
import Container from "@material-ui/core/Container";
import { i18n } from "../../translate/i18n";
import { AuthContext } from "../../context/Auth/AuthContext";
import ColorModeContext from "../../layout/themeContext";
import useSettings from "../../hooks/useSettings";
import IconButton from "@material-ui/core/IconButton";
import Brightness4Icon from "@material-ui/icons/Brightness4";
import Brightness7Icon from "@material-ui/icons/Brightness7";
import Visibility from "@material-ui/icons/Visibility";
import VisibilityOff from "@material-ui/icons/VisibilityOff";
import InputAdornment from "@material-ui/core/InputAdornment";
import { Helmet } from "react-helmet";
import BRFlag from "../../assets/brazil.png";
import USFlag from "../../assets/unitedstates.png";
import ESFlag from "../../assets/esspain.png";
import ARFlag from "../../assets/arabe.png";
import { getBackendUrl } from "../../config";
import packageJson from "../../../package.json";

const languageOptions = [
    { value: "pt-BR", label: "Português", icon: BRFlag },
    { value: "en", label: "English", icon: USFlag },
    { value: "es", label: "Spanish", icon: ESFlag },
    { value: "ar", label: "عربي", icon: ARFlag },
];

const useStyles = makeStyles((theme) => {
    return ({
    loginPageGlobal: {
        backgroundRepeat: "no-repeat !important",
        backgroundSize: "cover !important",
        backgroundPosition: "center center !important",
        backgroundAttachment: "fixed !important",
        width: "100% !important",
        height: "100% !important",
        minHeight: "100vh !important",
        minWidth: "100vw !important",
        overflow: "hidden !important",
    },
    root: {
        width: "100vw",
        height: "100vh",
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        padding: theme.spacing(3),
        margin: "0",
        boxSizing: "border-box",
        overflow: "auto",
        position: "relative",
        backgroundColor: theme.palette.background.default,
        backgroundImage: "none",
        backgroundRepeat: "no-repeat",
        backgroundSize: "cover",
        backgroundPosition: "center center",
        
        [theme.breakpoints.down("sm")]: {
            padding: theme.spacing(2),
        },
    },

    authFrame: {
        display: "grid",
        gridTemplateColumns: "minmax(0, 1.08fr) minmax(380px, 0.92fr)",
        width: "100%",
        minHeight: 640,
        overflow: "hidden",
        borderRadius: 24,
        backgroundColor: theme.palette.background.paper,
        border: `1px solid ${theme.palette.divider}`,
        boxShadow: theme.mode === "light"
            ? "0 24px 70px rgba(16, 24, 40, 0.10)"
            : "0 24px 70px rgba(0, 0, 0, 0.32)",
        [theme.breakpoints.down("sm")]: {
            display: "block",
            minHeight: "auto",
            borderRadius: 18,
        },
    },

    brandPanel: {
        position: "relative",
        display: "flex",
        flexDirection: "column",
        justifyContent: "space-between",
        padding: theme.spacing(6),
        overflow: "hidden",
        color: theme.palette.primary.contrastText,
        backgroundColor: theme.palette.primary.main,
        "&::before, &::after": {
            content: '""',
            position: "absolute",
            borderRadius: "50%",
            border: "1px solid rgba(255, 255, 255, 0.18)",
        },
        "&::before": {
            width: 360,
            height: 360,
            right: -130,
            top: -150,
        },
        "&::after": {
            width: 240,
            height: 240,
            left: -100,
            bottom: -100,
        },
        [theme.breakpoints.down("sm")]: {
            display: "none",
        },
    },

    brandMark: {
        position: "relative",
        zIndex: 1,
        width: "fit-content",
        padding: theme.spacing(1.5, 2),
        borderRadius: 12,
        backgroundColor: "rgba(255, 255, 255, 0.94)",
    },

    brandLogo: {
        display: "block",
        width: 190,
        maxHeight: 58,
        objectFit: "contain",
        content: "url(" + theme.calculatedLogoLight() + ")",
    },

    brandCopy: {
        position: "relative",
        zIndex: 1,
        maxWidth: 520,
        textAlign: "left",
    },

    brandEyebrow: {
        display: "inline-flex",
        padding: theme.spacing(0.75, 1.25),
        marginBottom: theme.spacing(2),
        borderRadius: 999,
        backgroundColor: "rgba(255, 255, 255, 0.14)",
        border: "1px solid rgba(255, 255, 255, 0.18)",
        fontSize: 12,
        fontWeight: 600,
        letterSpacing: "0.08em",
        textTransform: "uppercase",
    },

    brandTitle: {
        marginBottom: theme.spacing(2),
        color: "inherit",
        fontSize: "clamp(32px, 4vw, 52px)",
        lineHeight: 1.08,
        letterSpacing: "-0.04em",
        fontWeight: 700,
    },

    brandDescription: {
        maxWidth: 440,
        color: "rgba(255, 255, 255, 0.78)",
        fontSize: 16,
        lineHeight: 1.65,
    },

    brandFooter: {
        position: "relative",
        zIndex: 1,
        color: "rgba(255, 255, 255, 0.64)",
        fontSize: 12,
    },

    containerLogin: {
        padding: "0 !important",
        width: "100%",
        zIndex: 10,
        position: "relative",
        maxWidth: "1180px",
        margin: "0 auto",

        [theme.breakpoints.down("sm")]: {
            maxWidth: 520,
        },
    },

    paper: {
        display: "flex",
        flexDirection: "column",
        justifyContent: "center",
        alignItems: "stretch",
        padding: theme.spacing(7),
        width: "100%",
        position: "relative",
        boxSizing: "border-box",

        [theme.breakpoints.down("sm")]: {
            padding: theme.spacing(6, 4),
        },
        
        [theme.breakpoints.down("xs")]: {
            padding: theme.spacing(5, 3),
        },
    },

    "@keyframes slideInRight": {
        from: {
            opacity: 0,
            transform: "translateY(30px)",
        },
        to: {
            opacity: 1,
            transform: "translateY(0)",
        },
    },

    "@keyframes slideInUp": {
        from: {
            opacity: 0,
            transform: "translateY(30px)",
        },
        to: {
            opacity: 1,
            transform: "translateY(0)",
        },
    },

    avatar: {
        margin: theme.spacing(1),
        backgroundColor: "#3b82f6",
    },

    form: {
        width: "100%",
        marginTop: theme.spacing(1),
    },

    submit: {
        margin: theme.spacing(3, 0, 2),
        minHeight: 48,
        backgroundColor: theme.palette.primary.main,
        color: "white",
        borderRadius: "10px",
        padding: "12px 0",
        fontSize: "15px",
        fontWeight: 600,
        textTransform: "none",
        boxShadow: "none",
        border: "none",
        transition: "background-color 160ms ease, transform 160ms ease",
        "&:hover": {
            backgroundColor: theme.palette.primary.dark,
            boxShadow: "none",
            transform: "translateY(-1px)",
        },
        "&:active": {
            transform: "translateY(0)",
        },
    },

    powered: {
        color: "white",
    },

    logoImg: {
        display: "block",
        width: "100%",
        maxWidth: "210px",
        height: "auto",
        maxHeight: "64px",
        content: "url(" + theme.calculatedLogoLight() + ")",
    },

    mobileLogoMark: {
        display: "none",
        [theme.breakpoints.down("sm")]: {
            display: "flex",
            alignSelf: "center",
            justifyContent: "center",
            maxWidth: 250,
            margin: "0 auto 32px",
            padding: theme.spacing(1.25, 2),
            borderRadius: 12,
            backgroundColor: "rgba(255, 255, 255, 0.94)",
        },
    },

    formHeader: {
        marginBottom: theme.spacing(3),
        textAlign: "left",
    },

    formTitle: {
        marginBottom: theme.spacing(1),
        color: theme.palette.text.primary,
        fontSize: 30,
        lineHeight: 1.2,
        letterSpacing: "-0.035em",
        fontWeight: 700,
    },

    formDescription: {
        color: theme.palette.text.secondary,
        fontSize: 14,
        lineHeight: 1.6,
    },

    iconButton: {
        position: "absolute",
        top: 20,
        right: 20,
        width: 40,
        height: 40,
        backgroundColor: theme.palette.action.hover,
        border: `1px solid ${theme.palette.divider}`,
        color: theme.palette.text.secondary,
        transition: "background-color 160ms ease",
        "&:hover": {
            backgroundColor: theme.palette.action.selected,
        },
    },

    textField: {
        "& .MuiOutlinedInput-root": {
            borderRadius: "10px",
            minHeight: 48,
            backgroundColor: theme.mode === "light" ? theme.palette.background.paper : theme.palette.surfaceSunken,
            transition: "background-color 160ms ease, border-color 160ms ease",
            "&:hover": {
                backgroundColor: theme.mode === "light" ? theme.palette.background.paper : theme.palette.action.selected,
            },
            "&.Mui-focused": {
                backgroundColor: theme.mode === "light" ? theme.palette.background.paper : theme.palette.surfaceSunken,
                boxShadow: `0 0 0 3px ${theme.palette.primary.main}18`,
            },
            "& fieldset": {
                borderColor: theme.palette.borderStrong,
            },
            "&:hover fieldset": {
                borderColor: theme.palette.text.disabled,
            },
            "&.Mui-focused fieldset": {
                borderColor: theme.palette.primary.main,
                borderWidth: "2px",
            },
        },
        "& .MuiInputLabel-root": {
            color: theme.palette.text.secondary,
            fontWeight: 500,
            "&.Mui-focused": {
                color: theme.palette.primary.main,
            },
        },
    },

    languageSelector: {
        position: "fixed",
        top: "20px",
        left: "20px",
        zIndex: 1000,
        backgroundColor: theme.palette.background.paper,
        borderRadius: "10px",
        border: `1px solid ${theme.palette.divider}`,
        padding: "8px 12px",
        boxShadow: "0 1px 3px rgba(0, 0, 0, 0.08)",
        [theme.breakpoints.down("xs")]: {
            top: 12,
            left: 12,
        },
    },

    registerLink: {
        color: theme.palette.primary.main,
        textDecoration: "none",
        fontWeight: 600,
        transition: "all 0.3s ease",
        "&:hover": {
            color: theme.palette.primary.dark,
            textDecoration: "underline",
        },
    },

    languageDropdown: {
        display: "flex",
        alignItems: "center",
        background: "none",
        border: "none",
        color: theme.palette.text.primary,
        fontSize: "14px",
        fontWeight: 500,
        cursor: "pointer",
        gap: "8px",
        transition: "opacity 0.3s ease",
        "&:hover": {
            opacity: 0.8,
        },
    },

    languageOptions: {
        position: "absolute",
        top: "100%",
        left: "0",
        marginTop: "8px",
        background: "rgba(255, 255, 255, 0.95)",
        backdropFilter: "blur(20px)",
        boxShadow: "0 10px 25px rgba(0, 0, 0, 0.15)",
        borderRadius: "12px",
        border: "1px solid rgba(255, 255, 255, 0.2)",
        padding: "8px",
        zIndex: 1000,
        minWidth: "140px",
    },

    languageOption: {
        background: "none",
        border: "none",
        color: theme.palette.text.primary,
        display: "flex",
        alignItems: "center",
        gap: "8px",
        width: "100%",
        padding: "8px 12px",
        textAlign: "left",
        borderRadius: "8px",
        fontSize: "14px",
        fontWeight: 500,
        cursor: "pointer",
        transition: "all 0.2s ease",
        "&:hover": {
            background: "rgba(59, 130, 246, 0.1)",
            color: theme.palette.primary.main,
        },
    },

    flagIcon: {
        width: 20,
        height: 15,
        borderRadius: 2,
    },

    versionInfo: {
        position: "fixed",
        bottom: "20px",
        right: "20px",
        zIndex: 1000,
        backgroundColor: theme.palette.background.paper,
        borderRadius: "8px",
        border: `1px solid ${theme.palette.divider}`,
        padding: "6px 12px",
        color: theme.palette.text.secondary,
        fontSize: "12px",
        fontWeight: 500,
        opacity: 1,
        transition: "opacity 0.3s ease",
        "&:hover": {
            color: theme.palette.primary.main,
        },
        [theme.breakpoints.down("xs")]: {
            right: 12,
            bottom: 12,
        },
    },
})});

const Login = () => {
    const classes = useStyles();
    const theme = useTheme();
    const { colorMode } = useContext(ColorModeContext);
    const { appLogoFavicon, appName, mode } = colorMode;
    const [user, setUser] = useState({ email: "", password: "" });
    const [showPassword, setShowPassword] = useState(false);
    const [allowSignup, setAllowSignup] = useState(false);
    const { getPublicSetting } = useSettings();
    const { handleLogin } = useContext(AuthContext);

    const [open, setOpen] = useState(false);
    const ref = useRef();
    const [enabledLanguages, setEnabledLanguages] = useState(["pt-BR", "en"]);
    const [backgroundLight, setBackgroundLight] = useState("");
    const [backgroundDark, setBackgroundDark] = useState("");

    const getCompanyIdFromUrl = () => {
        const urlParams = new URLSearchParams(window.location.search);
        const companyId = urlParams.get("companyId");
        return companyId ? parseInt(companyId) : null;
    };

    const handleChangeInput = (e) => {
        setUser({ ...user, [e.target.name]: e.target.value });
    };

    const togglePasswordVisibility = () => {
        setShowPassword(!showPassword);
    };

    const handlSubmit = (e) => {
        e.preventDefault();
        handleLogin(user);
    };

    useEffect(() => {
        const companyId = getCompanyIdFromUrl();

        getPublicSetting("userCreation", companyId)
            .then((data) => {
                setAllowSignup(data === "enabled");
            })
            .catch((error) => {
                console.log("Error reading setting", error);
            });

        getPublicSetting("enabledLanguages", companyId)
            .then((langs) => {
                let arr = ["pt-BR", "en"];
                try {
                    if (langs) arr = JSON.parse(langs);
                } catch {}
                setEnabledLanguages(arr);
            })
            .catch(() => {
                setEnabledLanguages(["pt-BR", "en"]);
            });

        getPublicSetting("appLogoBackgroundLight", companyId)
            .then((bgLight) => {
                if (bgLight) {
                    const backendUrl = getBackendUrl();
                    const fullUrl = backendUrl + "/public/" + bgLight;
                    console.log("Background light URL:", fullUrl);
                    setBackgroundLight(fullUrl);
                } else {
                    setBackgroundLight("");
                }
            })
            .catch((err) => {
                console.error("Erro ao carregar imagem de fundo clara:", err);
                setBackgroundLight("");
            });

        getPublicSetting("appLogoBackgroundDark", companyId)
            .then((bgDark) => {
                if (bgDark) {
                    const backendUrl = getBackendUrl();
                    const fullUrl = backendUrl + "/public/" + bgDark;
                    console.log("Background dark URL:", fullUrl);
                    setBackgroundDark(fullUrl);
                } else {
                    setBackgroundDark("");
                }
            })
            .catch((err) => {
                console.error("Erro ao carregar imagem de fundo escura:", err);
                setBackgroundDark("");
            });
    }, []);

    const current =
        languageOptions.find((opt) => opt.value === i18n.language) ||
        languageOptions[0];

    const handleSelect = (opt) => {
        i18n.changeLanguage(opt.value);
        localStorage.setItem("language", opt.value);
        setOpen(false);
        window.location.reload();
    };

    let finalBackground;
    
    console.log("Modo:", mode);
    console.log("Background Light URL:", backgroundLight);
    console.log("Background Dark URL:", backgroundDark);
    
    const hasValidLightBg = backgroundLight && backgroundLight.trim() !== '';
    const hasValidDarkBg = backgroundDark && backgroundDark.trim() !== '';
    
    if (mode === "light") {
        if (hasValidLightBg) {
            finalBackground = `url(${backgroundLight})`;
            console.log("Usando background light:", finalBackground);
        } else {
            finalBackground = theme.palette.background ? theme.palette.background.default : "#f5f5f5";
            console.log("Usando cor do tema light:", finalBackground);
        }
    } else {
        if (hasValidDarkBg) {
            finalBackground = `url(${backgroundDark})`;
            console.log("Usando background dark:", finalBackground);
        } else {
            finalBackground = theme.palette.background ? theme.palette.background.default : "#303030";
            console.log("Usando cor do tema dark:", finalBackground);
        }
    }
    
    console.log("Background final definido:", finalBackground);


    return (
        <>
            <Helmet>
                <title>{appName || "AtendeChat"}</title>
                <link rel="icon" href={appLogoFavicon || "/default-favicon.ico"} />
            </Helmet>

            <div 
                className={classes.root}
                style={{
                    backgroundImage: typeof finalBackground === "string" && finalBackground.includes("url(") 
                        ? finalBackground 
                        : "none"
                }}
            >
                {/* Seletor de idioma */}
                <div
                    ref={ref}
                    className={classes.languageSelector}
                >
                    <button
                        onClick={() => setOpen((o) => !o)}
                        className={classes.languageDropdown}
                    >
                        <img
                            src={current.icon}
                            alt={current.label}
                            className={classes.flagIcon}
                        />
                        {current.label}
                        <span>▾</span>
                    </button>

                    {open && (
                        <div className={classes.languageOptions}>
                            {languageOptions
                                .filter((opt) => enabledLanguages.includes(opt.value))
                                .map((opt) => (
                                    <button
                                        key={opt.value}
                                        onClick={() => handleSelect(opt)}
                                        className={classes.languageOption}
                                    >
                                        <img
                                            src={opt.icon}
                                            alt={opt.label}
                                            className={classes.flagIcon}
                                        />
                                        {opt.label}
                                    </button>
                                ))}
                        </div>
                    )}
                </div>

                <Container
                    component="main"
                    maxWidth={false}
                    className={classes.containerLogin}
                >
                    <CssBaseline />
                    <div className={classes.authFrame}>
                        <section className={classes.brandPanel}>
                            <div className={classes.brandMark}>
                                <img className={classes.brandLogo} alt={appName || "Logo"} />
                            </div>
                            <div className={classes.brandCopy}>
                                <span className={classes.brandEyebrow}>{appName || "SIGIchat"}</span>
                                <Typography component="h1" className={classes.brandTitle}>
                                    {i18n.t("login.welcome")}
                                </Typography>
                                <Typography className={classes.brandDescription}>
                                    {i18n.t("login.loginInstruction")}
                                </Typography>
                            </div>
                            <Typography className={classes.brandFooter}>
                                {appName || "SIGIchat"} · v{packageJson.version}
                            </Typography>
                        </section>

                        <div className={classes.paper}>
                            <IconButton
                                className={classes.iconButton}
                                onClick={colorMode.toggleColorMode}
                                aria-label={mode === "dark" ? "Ativar tema claro" : "Ativar tema escuro"}
                            >
                                {mode === "dark" ? <Brightness7Icon /> : <Brightness4Icon />}
                            </IconButton>

                            <div className={classes.mobileLogoMark}>
                                <img className={classes.logoImg} alt={appName || "Logo"} />
                            </div>

                            <div className={classes.formHeader}>
                                <Typography component="h2" className={classes.formTitle}>
                                    {i18n.t("login.welcome")}
                                </Typography>
                                <Typography className={classes.formDescription}>
                                    {i18n.t("login.loginInstruction")}
                                </Typography>
                            </div>

                            <form className={classes.form} noValidate onSubmit={handlSubmit}>
                                <TextField
                                    variant="outlined"
                                    margin="normal"
                                    required
                                    fullWidth
                                    id="email"
                                    label={i18n.t("login.form.email")}
                                    name="email"
                                    value={user.email}
                                    onChange={handleChangeInput}
                                    autoComplete="email"
                                    autoFocus
                                    className={classes.textField}
                                />
                                <TextField
                                    variant="outlined"
                                    margin="normal"
                                    required
                                    fullWidth
                                    name="password"
                                    label={i18n.t("login.form.password")}
                                    type={showPassword ? "text" : "password"}
                                    id="password"
                                    value={user.password}
                                    onChange={handleChangeInput}
                                    autoComplete="current-password"
                                    className={classes.textField}
                                    InputProps={{
                                        endAdornment: (
                                            <InputAdornment position="end">
                                                <IconButton
                                                    aria-label="toggle password visibility"
                                                    onClick={togglePasswordVisibility}
                                                    edge="end"
                                                    style={{ color: theme.palette.text.secondary }}
                                                >
                                                    {showPassword ? <VisibilityOff /> : <Visibility />}
                                                </IconButton>
                                            </InputAdornment>
                                        ),
                                    }}
                                />
                                <Button
                                    type="submit"
                                    fullWidth
                                    variant="contained"
                                    color="primary"
                                    className={classes.submit}
                                >
                                    {i18n.t("login.buttons.submit")}
                                </Button>
                                {allowSignup && (
                                    <Grid container justifyContent="center">
                                        <Grid item>
                                            <Link
                                                href="#"
                                                variant="body2"
                                                component={RouterLink}
                                                to="/signup"
                                                className={classes.registerLink}
                                            >
                                                {i18n.t("login.buttons.register")}
                                            </Link>
                                        </Grid>
                                    </Grid>
                                )}
                            </form>
                        </div>
                    </div>
                </Container>

                {/* Informação da versão */}
                <Typography className={classes.versionInfo}>
                    v{packageJson.version}
                </Typography>
            </div>
        </>
    );
};

export default Login;
